<template>
  <div
    v-if="modifiedNode"
    tabindex="0"
    class="flex items-center cursor-pointer"
    :class="[
      selectedNodeId == modifiedNode.uid ? 'border-l-4 border-l-theme-400 bg-theme-50' : 'bg-white',
      table ? 'border-b' : '',
      options?.hasCheckboxes && modifiedNode.selected
        ? modifiedNode.parentId
          ? 'bg-theme-50'
          : 'bg-theme-100'
        : '',
    ]"
    @contextmenu.prevent="toggleDropdown">
    <div v-if="options?.hasCheckboxes" class="flex items-center px-2">
      <BaseCheckbox
        v-model="modifiedNode.selected"
        :icon="modifiedNode.selectIcon || 'check'"
        @update:model-value="onUpdateSelected" />
    </div>

    <button
      type="button"
      class="p-1"
      @click="handleExpandArrowClicked"
      v-if="!options?.hasCheckboxes">
      <Icon
        :icon="modifiedNode.expanded ? 'hugeicons:arrow-down-01' : 'hugeicons:arrow-right-01'"
        :class="modifiedNode.expanded ? 'text-theme-700' : ''"
        class="transition-all duration-100 ease-in"
        v-if="modifiedNode.children && modifiedNode.children.length" />
    </button>
    <button
      @click="handleOnAction({ action: 'clickNode', node: modifiedNode })"
      class="w-full p-2 text-sm font-medium text-left select-none"
      :class="options?.hasCheckboxes ? 'flex items-center justify-between' : ''">
      <span v-if="mode != 'rename' && modifiedNode.uid">
        {{ modifiedNode.label }}
      </span>
      <span
        v-if="options?.hasCheckboxes && modifiedNode.counter"
        class="inline-flex items-center rounded-full px-2 py-0.5 text-sm font-medium text-gray-800"
        :class="[modifiedNode.selected ? 'bg-white' : 'bg-theme-200']"
        >{{ modifiedNode.counter }}</span
      >
    </button>
    <button
      v-if="options?.hasCheckboxes"
      type="button"
      class="p-1"
      @click="handleExpandArrowClicked">
      <Icon
        :icon="modifiedNode.expanded ? 'hugeicons:arrow-down-01' : 'hugeicons:arrow-right-01'"
        :class="modifiedNode.expanded ? 'text-theme-700' : ''"
        class="transition-all duration-100 ease-in w-6 h-6"
        v-if="modifiedNode.children && modifiedNode.children.length" />
    </button>
    <input
      v-if="mode == 'rename'"
      type="text"
      ref="renameInput"
      v-model="modifiedNode.label"
      class="p-2 border border-theme-500 bg-theme-200 focus:outline-theme-500"
      @keyup.enter="onRename"
      @focusout="onRename" />
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted, watch, inject } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';
import { deepCopy } from '@/utils/data';

import { Icon } from '@iconify/vue';
import BaseCheckbox from './BaseCheckbox.vue';

import type {
  ContextMenuOptions,
  HandleTreeNodeOnActionProvider,
  TreeType,
} from '@/types/ComponentTypes';

const props = defineProps<{
  options?: {
    hasEditOption?: boolean;
    hasAddOption?: boolean;
    hasRenameOption?: boolean;
    hasDeleteOption?: boolean;
    hasDuplicateOption?: boolean;
    hasCheckboxes?: boolean;
  };
  mainTree?: boolean;
  inlineEditing?: boolean;
  editMode?: boolean;
  selectedNodeId?: string;
  node: TreeType;
  table?: boolean;
}>();

const modifiedNode = ref<TreeType>();

onMounted(() => {
  modifiedNode.value = deepCopy<TreeType>(props.node);
  if (props.node && !props.node.uid) onActionRename();

  if (props.mainTree && props.options?.hasAddOption) {
    contextMenuOptions.value.push({
      name: 'tree.addNode',
      action: 'add',
      icon: 'hugeicons:node-add',
    });
  }

  if (props.options?.hasAddOption) {
    contextMenuOptions.value.push({
      name: 'tree.addChildNode',
      action: 'addChild',
      icon: 'hugeicons:node-add',
    });
  }

  if (props.options?.hasEditOption) {
    contextMenuOptions.value.push({
      name: 'tree.edit',
      action: 'edit',
      icon: 'ic:baseline-edit',
    });
  }

  if (props.options?.hasRenameOption) {
    contextMenuOptions.value.push({
      name: 'tree.rename',
      action: 'rename',
      icon: 'fluent:rename-24-regular',
    });
  }

  if (props.options?.hasDuplicateOption) {
    contextMenuOptions.value.push({
      name: 'tree.duplicate',
      action: 'duplicate',
      icon: 'fad:duplicate',
    });
  }
  if (props.node.children.length) {
    contextMenuOptions.value.push({
      name: 'tree.fullExpand',
      action: 'expand',
      icon: 'fluent:arrow-expand-all-20-filled',
    });
    contextMenuOptions.value.push({
      name: 'tree.fullCollapse',
      action: 'collapse',
      icon: 'lucide:list-collapse',
    });
  }

  if (props.options?.hasDeleteOption) {
    contextMenuOptions.value.push({
      name: 'tree.deleteItem',
      action: 'delete',
      icon: 'tdesign:delete-1',
      color: 'text-red-500',
    });
  }
});

watch(
  () => props.node,
  () => {
    modifiedNode.value = deepCopy<TreeType>(props.node);
  }
);

const { t: $t } = useI18n();
const toastStore = useToastStore();

// INJECT HandleAction
const handleOnAction = inject<HandleTreeNodeOnActionProvider>('handleOnAction', () => {});

const mode = ref<'add' | 'rename' | 'addChild' | null>();

const toggleDropdown = (event: MouseEvent) => {
  if (!props.editMode) return;
  handleOnAction({
    action: 'toggleContextMenu',
    node: modifiedNode.value,
    clickLocation: { x: event.clientX, y: event.clientY },
    contextMenuOptions: contextMenuOptions.value,
  });
};

function handleExpandArrowClicked() {
  if (!mode.value) {
    handleOnAction({ action: 'expandNode', node: modifiedNode.value });
  }
}

function onActionRename() {
  mode.value = 'rename';
  nextTick().then(() => {
    renameInput.value?.focus();
    renameInput.value?.select();
    if (!props.inlineEditing) renameInput.value?.blur();
  });
}

const renameInput = ref();

function onRename() {
  if (modifiedNode.value && !modifiedNode.value?.uid) modifiedNode.value.uid = uuidv4();
  if (modifiedNode.value && !modifiedNode.value.label)
    toastStore.error({ text: $t('messages.errors.emptyNode') });
  else mode.value = null;
  handleOnAction({ action: 'rename', node: modifiedNode.value });
}

const contextMenuOptions = ref<ContextMenuOptions[]>([]);

function onUpdateSelected() {
  handleOnAction({ action: 'selectNode', node: modifiedNode.value });
}
</script>
